// Owns the two sidebar nav behaviours CSS can't express on its own.
//
// Overflow fades: the nav hides its scrollbar (see `.sidebar-nav`) so that
// expanding a group never shifts the rail sideways. That leaves no overflow
// cue, so the edges fade instead.
//
// Group flyouts: the nav is a scroll container, so an absolutely positioned
// flyout is clipped by it and never seen. These are `position: fixed` and get
// their coordinates here — anchored to the group's own right edge, with the
// padding stretched to where the card sits, so the pointer never crosses an
// unhovered gap on the way over.
const CLOSE_DELAY_MS = 180;
const FLYOUT_GAP_PX = 8;
const EDGE_MARGIN_PX = 8;

function isCollapsed() {
  return document.documentElement.classList.contains("sidebar-collapsed");
}

export const SidebarNav = {
  mounted() {
    this.scroller = this.el.closest(".sidebar-nav-scroll") || this.el;

    this.onScroll = () => {
      this.syncFades();
      this.closeFlyout();
    };
    this.onPointerOver = (e) => {
      const group = e.target.closest("[data-nav-group]");
      if (group) this.openFlyout(group);
    };
    this.onPointerOut = (e) => {
      const group = e.target.closest("[data-nav-group]");
      // The flyout renders outside its group but is still a descendant of it,
      // so moving into the flyout is not leaving the group.
      if (group && !group.contains(e.relatedTarget)) this.scheduleClose();
    };
    this.onClick = (e) => {
      if (!isCollapsed()) return;
      if (!e.target.closest("[data-nav-group] button")) return;

      this.closeFlyout();
      window.dispatchEvent(new CustomEvent("sidebar:expand"));
    };
    this.onCollapseChange = () => {
      this.closeFlyout();
      this.syncFades();
    };

    this.el.addEventListener("scroll", this.onScroll, { passive: true });
    this.el.addEventListener("mouseover", this.onPointerOver);
    this.el.addEventListener("mouseout", this.onPointerOut);
    this.el.addEventListener("click", this.onClick);
    window.addEventListener("sidebar:collapsed", this.onCollapseChange);

    this.resizeObserver = new ResizeObserver(() => this.syncFades());
    this.resizeObserver.observe(this.el);

    this.syncFades();
  },

  updated() {
    this.syncFades();
  },

  destroyed() {
    clearTimeout(this.closeTimer);
    this.el.removeEventListener("scroll", this.onScroll);
    this.el.removeEventListener("mouseover", this.onPointerOver);
    this.el.removeEventListener("mouseout", this.onPointerOut);
    this.el.removeEventListener("click", this.onClick);
    window.removeEventListener("sidebar:collapsed", this.onCollapseChange);
    this.resizeObserver?.disconnect();
  },

  syncFades() {
    const { scrollTop, clientHeight, scrollHeight } = this.el;
    this.scroller.toggleAttribute("data-overflow-top", scrollTop > 2);
    this.scroller.toggleAttribute(
      "data-overflow-bottom",
      scrollTop + clientHeight < scrollHeight - 2,
    );
  },

  openFlyout(group) {
    clearTimeout(this.closeTimer);

    const flyout = group.querySelector("[data-nav-flyout]");
    if (!flyout || flyout === this.flyout) return;
    // Group flyouts stand in for labels the collapsed rail hides. The support
    // menu has no expanded equivalent, so it opens in either state.
    if (flyout.dataset.navFlyoutWhen === "collapsed" && !isCollapsed()) return;

    this.closeFlyout();

    const rail = this.el.closest('[data-sidebar="desktop"]');
    if (!rail) return;

    const groupRect = group.getBoundingClientRect();
    const railRight = rail.getBoundingClientRect().right;

    flyout.style.left = `${groupRect.right}px`;
    flyout.style.paddingLeft = `${Math.max(FLYOUT_GAP_PX, railRight + FLYOUT_GAP_PX - groupRect.right)}px`;
    flyout.hidden = false;

    // Height is only measurable once it is laid out, so top comes last.
    const height = flyout.getBoundingClientRect().height;
    const maxTop = window.innerHeight - height - EDGE_MARGIN_PX;
    flyout.style.top = `${Math.max(EDGE_MARGIN_PX, Math.min(groupRect.top - 6, maxTop))}px`;

    this.flyout = flyout;
  },

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeFlyout(), CLOSE_DELAY_MS);
  },

  closeFlyout() {
    if (!this.flyout) return;
    this.flyout.hidden = true;
    this.flyout = null;
  },
};
